import React, { useEffect, useMemo, useState } from "react";

import { Loader } from "../loaders/loaders";
import { getApi } from "../../apis/api";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { useMessage } from "../../context/MessageContext";

import {
  DocumentBlueIcon,
  ModalCloseIcon,
  ModalItemExpandIcon,
} from "../../assets";

export interface DeliveryStatusItem {
  email: string;
  status: string;
}

export interface AccordionItem {
  [key: string]: string | number | DeliveryStatusItem[] | undefined;
}

export interface AccordionFieldConfig {
  text: string;
  className?: string | ((item: AccordionItem) => string);
  hidden?: (item: AccordionItem) => boolean;
}

export interface AccordionFieldsObject {
  leftColumn: AccordionFieldConfig[];
  rightColumn: AccordionFieldConfig[];
}

export interface AccordionField {
  heading: {
    text: string;
    createdAt: string;
  };
  fields: AccordionFieldsObject;
  deliveryStatus?: {
    key: string;
    title: string;
  };
}

/**
 * A single column of the flat table layout (see `ModalTableConfig`).
 */
export interface ModalTableColumn {
  /** Column header label. */
  header: string;
  /** Key read from the mapped item to resolve the cell value. */
  key: string;
  /** Render the value as a coloured status badge instead of plain text. */
  badge?: boolean;
}

/**
 * Opt-in flat table layout for the modal. When a dropdown config provides
 * `tableConfig`, the modal renders a simple column/row table instead of the
 * default accordion — useful for histories that only carry a couple of fields.
 */
export interface ModalTableConfig {
  columns: ModalTableColumn[];
}

/**
 * Resolves a recipient delivery status value to a badge variant used for styling.
 */
const getDeliveryStatusVariant = (status?: string): string => {
  switch (String(status ?? "").toLowerCase()) {
    case "delivered":
    case "success":
      return "delivered";
    case "sent":
      return "sent";
    case "opened":
      return "opened";
    case "failed":
      return "failed";
    case "bounced":
      return "bounced";
    case "complaint":
      return "complaint";
    default:
      return "default";
  }
};

/**
 * Capitalizes the first letter of a string (e.g. "delivered" -> "Delivered").
 */
const capitalizeStatus = (value?: string): string => {
  const str = String(value ?? "");
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
};

/**
 * Safely extracts a scalar (string/number) value from an accordion item field
 * for rendering, ignoring array values such as the recipient delivery status.
 */
const getScalarValue = (
  value: string | number | DeliveryStatusItem[] | undefined
): string | number => {
  if (Array.isArray(value) || value === undefined) {
    return "";
  }
  return value;
};

interface DropdownItemTypes {
  name: string;
  apiUrl: string;
  icon?: React.ReactElement;
  accordionFields?: AccordionField;
  tableConfig?: ModalTableConfig;
  mappingFunction?: (data: any) => AccordionItem[];
}

interface DropdownModalProps {
  onClose: () => void;
  config: DropdownItemTypes;
  id: string;
}

/**
 * DropdownModal Component
 * This component is responsible for rendering a modal with accordion data fetched from an API.
 *
 * @param onClose - A function to close the modal
 * @param config - Configuration object for the modal (includes API URL, fields, etc.)
 * @param id - The ID used to fetch data from the API
 */
const DropdownModal: React.FC<DropdownModalProps> = ({
  onClose,
  config,
  id,
}) => {
  const { setMessage } = useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(
    null
  );
  const [accordionData, setAccordionData] = useState<AccordionItem[]>([]);

  /**
   * Toggle the accordion state to open or close based on the clicked index.
   *
   * @param index - The index of the accordion item to toggle
   */
  const toggleAccordion = (index: number) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  /**
   * Fetches data from the API using the provided URL and ID.
   * If the response is successful and contains data, it maps the data using the provided mapping function (if available).
   */
  const getModalData = async () => {
    if (!config.apiUrl || !id) {
      return;
    }
    try {
      setLoading(true);
      // Assuming there's an endpoint for fetching record data with pagination
      const response = await getApi(`${config.apiUrl}/${id}`);
      if (response.success && Array.isArray(response.data)) {
        const data = response?.data || [];
        if (config.mappingFunction) {
          const mappedData = config.mappingFunction(data);
          setAccordionData(mappedData);
        }
      } else {
        setMessage(response?.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      console.log("error", error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * useEffect hook to fetch data when the component is mounted or when the ID changes.
   */
  useEffect(() => {
    getModalData();
  }, []);

  /**
   * Renders the fields inside the left and right columns.
   *
   * @param {AccordionFieldsObject} fields - The fields to be rendered
   * @param {AccordionItem} item - The data for the current accordion item
   * @param {string} column - The column type (leftColumn or rightColumn)
   */
  const renderFields = (
    fields: AccordionFieldsObject,
    item: AccordionItem,
    column: "leftColumn" | "rightColumn"
  ) => {
    return (fields?.[column] ?? []).map((field, index) => {
      // Allow a field to opt out of rendering for a specific item (e.g. "To Mail"
      // is only shown when the recipient delivery status is unavailable).
      if (typeof field?.hidden === "function" && field.hidden(item)) {
        return null;
      }
      const resolvedClassName =
        typeof field?.className === "function"
          ? field.className(item)
          : field?.className;
      return (
        <div className="orderPaymentHistory__item" key={index}>
          <div className="text">{field.text}</div>
          <div
            className={`value ${
              resolvedClassName ? `value--${resolvedClassName}` : ""
            }`}
          >
            :{" "}
            {field.text === "Total Service Cost" ||
            field.text === "Total Cust. Charges"
              ? "$" + getScalarValue(item?.[field.text])
              : getScalarValue(item?.[field.text])}
          </div>
        </div>
      );
    });
  };

  /**
   * Renders the recipient delivery status table for an accordion item, when the
   * configuration defines a delivery status section and the item has recipients.
   *
   * @param {AccordionItem} item - The data for the current accordion item
   */
  const renderDeliveryStatus = (item: AccordionItem) => {
    const deliveryStatusConfig = config.accordionFields?.deliveryStatus;
    if (!deliveryStatusConfig) {
      return null;
    }
    const recipients = item?.[deliveryStatusConfig.key];
    if (!Array.isArray(recipients) || recipients.length === 0) {
      return null;
    }
    return (
      <div className="recipientDeliveryStatus">
        <h5 className="recipientDeliveryStatus__title">
          {deliveryStatusConfig.title}
        </h5>
        <div className="recipientDeliveryStatus__table">
          <div className="recipientDeliveryStatus__row recipientDeliveryStatus__row--head">
            <div className="recipientDeliveryStatus__cell">Email</div>
            <div className="recipientDeliveryStatus__cell">Delivery Status</div>
          </div>
          {recipients.map((recipient, rIndex) => (
            <div className="recipientDeliveryStatus__row" key={rIndex}>
              <div className="recipientDeliveryStatus__cell">
                {recipient?.email ?? ""}
              </div>
              <div className="recipientDeliveryStatus__cell">
                <span
                  className={`statusBadge statusBadge--${getDeliveryStatusVariant(
                    recipient?.status
                  )}`}
                >
                  {capitalizeStatus(recipient?.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renders a single cell value, as a status badge when the column asks for it.
   *
   * @param {ModalTableColumn} column - The column being rendered
   * @param {AccordionItem} item - The data for the current row
   */
  const renderTableCell = (column: ModalTableColumn, item: AccordionItem) => {
    const value = getScalarValue(item?.[column.key]);
    // Plain text for non-badge columns, and for badge columns with no value
    // (an empty pill would read as a status of its own).
    if (!column.badge || value === "") {
      return value;
    }
    return (
      <span
        className={`statusBadge statusBadge--${getDeliveryStatusVariant(
          String(value)
        )}`}
      >
        {capitalizeStatus(String(value))}
      </span>
    );
  };

  /**
   * Memoize the flat table layout, used only when the config opts into it via
   * `tableConfig`. Columns are driven entirely by configuration.
   */
  const tableList = useMemo(() => {
    const columns = config.tableConfig?.columns;
    if (!columns?.length) {
      return null;
    }
    if (accordionData.length === 0) {
      return <div className="noDataFound">No data found</div>;
    }
    const gridTemplateColumns = `repeat(${columns.length}, minmax(0, 1fr))`;
    return (
      <div className="modalTable">
        <div
          className="modalTable__row modalTable__row--head"
          style={{ gridTemplateColumns }}
        >
          {columns.map((column) => (
            <div className="modalTable__cell" key={column.key}>
              {column.header}
            </div>
          ))}
        </div>
        {accordionData.map((item, index) => (
          <div
            className="modalTable__row"
            style={{ gridTemplateColumns }}
            key={`modal-table-row-${index}`}
          >
            {columns.map((column) => (
              <div className="modalTable__cell" key={column.key}>
                {renderTableCell(column, item)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionData, config]);

  /**
   * Memoize accordion list to avoid unnecessary recalculations on each render.
   */
  const accordionList = useMemo(() => {
    // Table-driven configs (e.g. EDI history) render via `tableList` instead.
    if (!config.accordionFields) {
      return null;
    }
    if (accordionData.length === 0) {
      return <div className="noDataFound">No data found</div>;
    }
    // console.log("accordionData----------", accordionData, config);

    return accordionData.map((item, index) => (
      <div
        className={`accordionList__item ${
          openAccordionIndex === index ? "active" : ""
        }`}
        key={index}
      >
        <h4 className="accordionHeading" onClick={() => toggleAccordion(index)}>
          <span className="title">
            <span className="icon icon--w40">
              <DocumentBlueIcon />
            </span>
            <span className="titleInfo">
              <span className="title">
                {config.accordionFields?.heading.text}:{" "}
                <span>
                  {config.accordionFields?.heading
                    ? getScalarValue(item[config.accordionFields?.heading.text])
                    : ""}
                </span>
              </span>
              {config.name === "Order History" ? (
                <></>
              ) : (
                <span className="title">
                  {config.accordionFields?.heading.createdAt}:{" "}
                  <span>
                    {config.accordionFields?.heading
                      ? getScalarValue(
                          item[config.accordionFields?.heading.createdAt]
                        )
                      : ""}
                  </span>
                </span>
              )}
            </span>
          </span>
          <span className="iconArrow">
            <ModalItemExpandIcon />
          </span>
        </h4>
        <div className="accordionData">
          <div className="orderPaymentHistory">
            <div className="orderPaymentHistory__row">
              <div className="orderPaymentHistory__col">
                {renderFields(
                  config.accordionFields?.fields as AccordionFieldsObject,
                  item,
                  "leftColumn"
                )}
              </div>
              <div className="orderPaymentHistory__col">
                {renderFields(
                  config.accordionFields?.fields as AccordionFieldsObject,
                  item,
                  "rightColumn"
                )}
              </div>
            </div>
            {renderDeliveryStatus(item)}
          </div>
        </div>
      </div>
    ));
  }, [accordionData, openAccordionIndex, config]);

  if (loading) return <Loader />;

  return (
    <>
      <div className="modal modal--hazmatDetail modal--open">
        <div className="modal__dialog">
          <div className="modal__content modal__content--lg">
            <div className="modal__header">
              <h4 className="modal__header--title">{config?.name}</h4>
              <button
                className="btn btn__transparent btnClose"
                onClick={onClose}
              >
                <ModalCloseIcon />
              </button>
            </div>
            <div className="modal__body modal-body-left">
              {config.tableConfig ? (
                tableList
              ) : (
                <div className="accordionList">{accordionList}</div>
              )}
            </div>
          </div>
        </div>
        <div className="modal__backdrop"></div>
      </div>
    </>
  );
};

export default DropdownModal;
